// lib/websocket-context.tsx - COMPLETE FIXED VERSION (SINGLETON PATTERN)
"use client";

import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from "react";
import { io, Socket } from "socket.io-client";
import { authClient } from "@/lib/auth-client";

interface OnlineUser {
  userId: string;
  socketId: string;
  profile: any;
}

interface ChatMessage {
  id: number;
  sender: string;
  title_id: number;
  text: string;
  sent_at: string;
  users: {
    id: string;
    name: string;
    email: string;
    avatar?: string;
  };
}

interface WebSocketContextType {
  socket: Socket | null;
  isConnected: boolean;
  onlineUsers: OnlineUser[];
  sendMessage: (data: {
    chatId: number;
    text: string;
    attachments?: string;
    attachmentsType?: number;
  }) => Promise<void>;
  joinChat: (chatId: number) => void;
  leaveChat: (chatId: number) => void;
  setTyping: (chatId: number, isTyping: boolean) => void;
  addMessageListener: (chatId: number, callback: (message: ChatMessage) => void) => void;
  removeMessageListener: (chatId: number) => void;
  getMessagesForChat: (chatId: number, limit?: number) => Promise<ChatMessage[]>;
  emitEvent: (event: string, data: any, callback?: (response: any) => void) => void;
  addEventListener: (event: string, callback: (data: any) => void) => void;
  removeEventListener: (event: string, callback?: (data: any) => void) => void;
  disconnect: () => void;
  reconnect: () => void;
}

const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined);

// Global singleton to track WebSocket instance
declare global {
  interface Window {
    __SOCKET_INSTANCE__?: Socket;
    __SOCKET_REFERENCES__?: number;
  }
}

export function WebSocketProvider({ children }: { children: React.ReactNode }) {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState<OnlineUser[]>([]);
  
  const messageListeners = useRef<Map<number, ((message: ChatMessage) => void)[]>>(new Map());
  const eventListeners = useRef<Map<string, ((data: any) => void)[]>>(new Map());
  const reconnectTimer = useRef<NodeJS.Timeout | null>(null);
  const pingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastSessionId = useRef<string | null>(null);
  const isMounted = useRef(true);
  const connectionAttempts = useRef(0);
  const MAX_CONNECTION_ATTEMPTS = 5;
  
  const { data: session } = authClient.useSession();

  // Initialize WebSocket connection with singleton pattern
  const initializeSocket = useCallback(() => {
    if (typeof window === 'undefined' || !isMounted.current) return;

    // Check if we already have a global socket instance
    if (window.__SOCKET_INSTANCE__ && window.__SOCKET_INSTANCE__.connected) {
      console.log("🔄 Using existing WebSocket connection");
      const existingSocket = window.__SOCKET_INSTANCE__;
      setSocket(existingSocket);
      setIsConnected(existingSocket.connected);
      
      // Increment reference count
      window.__SOCKET_REFERENCES__ = (window.__SOCKET_REFERENCES__ || 0) + 1;
      console.log(`📊 Socket references: ${window.__SOCKET_REFERENCES__}`);
      
      return existingSocket;
    }

    // Check if we should attempt to reconnect
    if (connectionAttempts.current >= MAX_CONNECTION_ATTEMPTS) {
      console.error("❌ Max connection attempts reached");
      return null;
    }

    connectionAttempts.current++;
    
    try {
      console.log("🔄 Creating new WebSocket connection...");
      
      const socketInstance = io({
        path: "/socket.io/",
        transports: ["websocket", "polling"],
        autoConnect: true,
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        timeout: 20000,
      });

      // Store globally as singleton
      window.__SOCKET_INSTANCE__ = socketInstance;
      window.__SOCKET_REFERENCES__ = 1;

      // Connection events
      const handleConnect = () => {
        if (!isMounted.current) return;
        
        console.log("✅ WebSocket connected:", socketInstance.id);
        setIsConnected(true);
        connectionAttempts.current = 0;
        
        // Clear reconnect timer
        if (reconnectTimer.current) {
          clearTimeout(reconnectTimer.current);
          reconnectTimer.current = null;
        }
        
        // Add user to online users if session exists
        if (session?.user?.id) {
          const sessionId = String(session.user.id);
          if (lastSessionId.current !== sessionId) {
            console.log("👤 Adding user to online users:", sessionId);
            socketInstance.emit("addNewUser", {
              user: {
                id: sessionId,
                name: session.user.name,
                email: session.user.email,
                avatar: session.user.image,
              }
            });
            lastSessionId.current = sessionId;
          }
        }
        
        // Start ping interval to keep connection alive
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
        }
        pingIntervalRef.current = setInterval(() => {
          if (socketInstance.connected && isMounted.current) {
            socketInstance.emit("ping");
          }
        }, 25000); // Every 25 seconds
      };

      const handleConnectError = (error: any) => {
        console.error("❌ WebSocket connection error:", error.message);
        if (isMounted.current) {
          setIsConnected(false);
        }
      };

      const handleDisconnect = (reason: string) => {
        console.log("❌ WebSocket disconnected:", reason);
        if (isMounted.current) {
          setIsConnected(false);
        }
        
        // Clear ping interval
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }
        
        // Don't auto-reconnect if manually disconnected
        if (reason !== "io client disconnect" && reason !== "transport close") {
          if (reconnectTimer.current) {
            clearTimeout(reconnectTimer.current);
          }
          reconnectTimer.current = setTimeout(() => {
            if (isMounted.current && socketInstance && !socketInstance.connected) {
              console.log("🔄 Attempting to reconnect...");
              socketInstance.connect();
            }
          }, 3000);
        }
      };

      // Online users management
      const handleGetUsers = (users: OnlineUser[]) => {
        if (!isMounted.current) return;
        
        console.log("👥 Online users updated:", users.length);
        setOnlineUsers(users);
        
        const listeners = eventListeners.current.get("onlineUsers") || [];
        listeners.forEach(listener => {
          try {
            listener(users);
          } catch (error) {
            console.error("Error in onlineUsers listener:", error);
          }
        });
      };

      // New message event
      const handleNewMessage = (message: any) => {
        if (!isMounted.current) return;
        
        console.log("📨 New message received via WebSocket:", {
          id: message.id,
          chatId: message.title_id,
          sender: message.sender,
          text: message.text?.substring(0, 30)
        });

        const formattedMessage: ChatMessage = {
          id: message.id,
          sender: String(message.sender || ""),
          title_id: Number(message.title_id || message.chatId || 0),
          text: String(message.text || ""),
          sent_at: String(message.sent_at || new Date().toISOString()),
          users: {
            id: String(message.users?.id || message.sender || ""),
            name: String(message.users?.name || "Unknown"),
            email: String(message.users?.email || ""),
            avatar: message.users?.avatar,
          }
        };

        const chatId = formattedMessage.title_id;
        const listeners = messageListeners.current.get(chatId) || [];
        listeners.forEach(listener => {
          try {
            listener(formattedMessage);
          } catch (error) {
            console.error("Error in message listener:", error);
          }
        });
        
        const generalListeners = eventListeners.current.get("new-message") || [];
        generalListeners.forEach(listener => {
          try {
            listener(formattedMessage);
          } catch (error) {
            console.error("Error in new-message listener:", error);
          }
        });
      };

      const handleMessageSent = (data: any) => {
        console.log("✓ Message sent confirmation:", data);
      };

      const handleChatUpdated = (data: any) => {
        if (!isMounted.current) return;
        
        console.log("🔄 Chat updated via WebSocket:", data);
        const listeners = eventListeners.current.get("chat-updated") || [];
        listeners.forEach(listener => {
          try {
            listener(data);
          } catch (error) {
            console.error("Error in chat-updated listener:", error);
          }
        });
      };

      const handleError = (error: any) => {
        console.error("❌ WebSocket error:", error);
      };

      // Attach event listeners
      socketInstance.on("connect", handleConnect);
      socketInstance.on("connect_error", handleConnectError);
      socketInstance.on("disconnect", handleDisconnect);
      socketInstance.on("getUsers", handleGetUsers);
      socketInstance.on("new-message", handleNewMessage);
      socketInstance.on("message-sent", handleMessageSent);
      socketInstance.on("chat-updated", handleChatUpdated);
      socketInstance.on("error", handleError);

      setSocket(socketInstance);
      
      // Store cleanup functions
      socketInstance._cleanup = () => {
        socketInstance.off("connect", handleConnect);
        socketInstance.off("connect_error", handleConnectError);
        socketInstance.off("disconnect", handleDisconnect);
        socketInstance.off("getUsers", handleGetUsers);
        socketInstance.off("new-message", handleNewMessage);
        socketInstance.off("message-sent", handleMessageSent);
        socketInstance.off("chat-updated", handleChatUpdated);
        socketInstance.off("error", handleError);
      };
      
      return socketInstance;
    } catch (error) {
      console.error("❌ Failed to initialize WebSocket:", error);
      return null;
    }
  }, []); // Empty dependencies - initialize once

  // Initialize on mount
  useEffect(() => {
    isMounted.current = true;
    
    // Initialize socket
    const socketInstance = initializeSocket();
    
    // Set initial connected state
    if (socketInstance?.connected) {
      setIsConnected(true);
    }

    return () => {
      isMounted.current = false;
      
      // Decrement reference count
      if (window.__SOCKET_REFERENCES__) {
        window.__SOCKET_REFERENCES__--;
        console.log(`📊 Socket references: ${window.__SOCKET_REFERENCES__}`);
        
        // Only cleanup if no references left
        if (window.__SOCKET_REFERENCES__ <= 0) {
          console.log("🧹 No more references, cleaning up socket...");
          
          // Clear intervals
          if (pingIntervalRef.current) {
            clearInterval(pingIntervalRef.current);
            pingIntervalRef.current = null;
          }
          
          if (reconnectTimer.current) {
            clearTimeout(reconnectTimer.current);
            reconnectTimer.current = null;
          }
          
          // Clean up global socket if it exists
          if (window.__SOCKET_INSTANCE__) {
            const globalSocket = window.__SOCKET_INSTANCE__;
            
            // Call cleanup function if it exists
            if ((globalSocket as any)._cleanup) {
              (globalSocket as any)._cleanup();
            }
            
            // Disconnect socket
            if (globalSocket.connected) {
              globalSocket.disconnect();
            }
            
            // Remove from window
            delete window.__SOCKET_INSTANCE__;
            delete window.__SOCKET_REFERENCES__;
          }
        }
      }
      
      // Clear local listeners
      messageListeners.current.clear();
      eventListeners.current.clear();
    };
  }, [initializeSocket]);

  // Update user status when session changes
  useEffect(() => {
    if (!socket || !isConnected || !session?.user?.id || !isMounted.current) return;

    const sessionId = String(session.user.id);
    if (lastSessionId.current === sessionId) return;
    
    const timer = setTimeout(() => {
      console.log("🔄 Updating user session in WebSocket");
      socket.emit("addNewUser", {
        user: {
          id: sessionId,
          name: session.user.name,
          email: session.user.email,
          avatar: session.user.image,
        }
      });
      lastSessionId.current = sessionId;
    }, 500);

    return () => clearTimeout(timer);
  }, [session, socket, isConnected]);

  // WebSocket functions
  const sendMessage = useCallback(async (data: {
    chatId: number;
    text: string;
    attachments?: string;
    attachmentsType?: number;
  }) => {
    if (!socket || !isConnected || !session?.user?.id) {
      throw new Error("WebSocket not ready");
    }

    console.log("📤 Sending message via WebSocket:", {
      chatId: data.chatId,
      text: data.text?.substring(0, 50),
      sender: session.user.id
    });

    return new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error("Message send timeout"));
      }, 5000);

      const messageData = {
        sender: String(session.user.id),
        chatId: Number(data.chatId),
        text: String(data.text),
        attachments: data.attachments,
        attachmentsType: data.attachmentsType,
      };

      socket.emit("send-message", messageData, (ack: any) => {
        clearTimeout(timeout);
        
        if (ack && ack.success) {
          console.log("✅ Message sent successfully (server ack):", ack);
          resolve();
        } else {
          const errorMessage = ack?.message || "Failed to send message";
          console.error("❌ Message sending failed (server ack):", errorMessage);
          reject(new Error(errorMessage));
        }
      });
    });
  }, [socket, isConnected, session]);

  const joinChat = useCallback((chatId: number) => {
    if (!socket || !isConnected) {
      console.error("Cannot join chat: WebSocket not ready");
      return;
    }

    console.log("➕ Joining chat:", chatId);
    socket.emit("join-chat", chatId, (response: any) => {
      if (response?.success) {
        console.log("✅ Joined chat:", chatId);
      } else {
        console.error("❌ Failed to join chat:", chatId, response);
      }
    });
  }, [socket, isConnected]);

  const leaveChat = useCallback((chatId: number) => {
    if (!socket || !isConnected) return;
    
    console.log("➖ Leaving chat:", chatId);
    socket.emit("leave-chat", chatId, (response: any) => {
      if (response?.success) {
        console.log("✅ Left chat:", chatId);
      } else {
        console.error("❌ Failed to leave chat:", chatId, response);
      }
    });
  }, [socket, isConnected]);

  const setTyping = useCallback((chatId: number, isTyping: boolean) => {
    if (!socket || !isConnected || !session?.user?.id) return;
    
    socket.emit("typing", {
      chatId: Number(chatId),
      userId: String(session.user.id),
      isTyping: Boolean(isTyping),
    });
  }, [socket, isConnected, session]);

  const getMessagesForChat = useCallback(async (chatId: number, limit: number = 50): Promise<ChatMessage[]> => {
    if (!socket || !isConnected) {
      throw new Error("WebSocket not ready");
    }

    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error("Get messages timeout"));
      }, 10000);

      socket.emit("get-messages", chatId, limit, (response: any) => {
        clearTimeout(timeout);
        
        if (response?.success && response.messages) {
          console.log(`✅ Got ${response.messages.length} messages via WebSocket for chat ${chatId}`);
          resolve(response.messages);
        } else {
          const errorMessage = response?.message || "Failed to get messages";
          console.error("❌ Failed to get messages:", errorMessage);
          reject(new Error(errorMessage));
        }
      });
    });
  }, [socket, isConnected]);

  const emitEvent = useCallback((event: string, data: any, callback?: (response: any) => void) => {
    if (!socket || !isConnected) {
      console.error("Cannot emit event: WebSocket not ready");
      return;
    }

    if (callback) {
      socket.emit(event, data, callback);
    } else {
      socket.emit(event, data);
    }
  }, [socket, isConnected]);

  const addMessageListener = useCallback((chatId: number, callback: (message: ChatMessage) => void) => {
    console.log(`👂 Adding message listener for chat ${chatId}`);
    if (!messageListeners.current.has(chatId)) {
      messageListeners.current.set(chatId, []);
    }
    const listeners = messageListeners.current.get(chatId)!;
    if (!listeners.includes(callback)) {
      listeners.push(callback);
    }
  }, []);

  const removeMessageListener = useCallback((chatId: number) => {
    console.log(`👋 Removing message listener for chat ${chatId}`);
    messageListeners.current.delete(chatId);
  }, []);

  const addEventListener = useCallback((event: string, callback: (data: any) => void) => {
    console.log(`👂 Adding event listener for ${event}`);
    if (!eventListeners.current.has(event)) {
      eventListeners.current.set(event, []);
    }
    const listeners = eventListeners.current.get(event)!;
    if (!listeners.includes(callback)) {
      listeners.push(callback);
    }
  }, []);

  const removeEventListener = useCallback((event: string, callback?: (data: any) => void) => {
    console.log(`👋 Removing event listener for ${event}`);
    if (callback) {
      const listeners = eventListeners.current.get(event) || [];
      const filtered = listeners.filter(listener => listener !== callback);
      eventListeners.current.set(event, filtered);
    } else {
      eventListeners.current.delete(event);
    }
  }, []);

  const disconnect = useCallback(() => {
    if (socket && socket.connected) {
      console.log("🔌 Manually disconnecting WebSocket");
      socket.disconnect();
    }
  }, [socket]);

  const reconnect = useCallback(() => {
    if (socket && !socket.connected) {
      console.log("🔄 Manually reconnecting WebSocket");
      connectionAttempts.current = 0;
      socket.connect();
    }
  }, [socket]);

  // Context value
  const contextValue: WebSocketContextType = React.useMemo(() => ({
    socket,
    isConnected,
    onlineUsers,
    sendMessage,
    joinChat,
    leaveChat,
    setTyping,
    addMessageListener,
    removeMessageListener,
    getMessagesForChat,
    emitEvent,
    addEventListener,
    removeEventListener,
    disconnect,
    reconnect,
  }), [
    socket,
    isConnected,
    onlineUsers,
    sendMessage,
    joinChat,
    leaveChat,
    setTyping,
    addMessageListener,
    removeMessageListener,
    getMessagesForChat,
    emitEvent,
    addEventListener,
    removeEventListener,
    disconnect,
    reconnect,
  ]);

  return (
    <WebSocketContext.Provider value={contextValue}>
      {children}
    </WebSocketContext.Provider>
  );
}

export function useWebSocket() {
  const context = useContext(WebSocketContext);
  
  if (!context) {
    return {
      socket: null,
      isConnected: false,
      onlineUsers: [],
      sendMessage: () => Promise.reject(new Error("WebSocket not initialized")),
      joinChat: () => console.warn("WebSocket not initialized"),
      leaveChat: () => console.warn("WebSocket not initialized"),
      setTyping: () => console.warn("WebSocket not initialized"),
      addMessageListener: () => console.warn("WebSocket not initialized"),
      removeMessageListener: () => console.warn("WebSocket not initialized"),
      getMessagesForChat: () => Promise.reject(new Error("WebSocket not initialized")),
      emitEvent: () => console.warn("WebSocket not initialized"),
      addEventListener: () => console.warn("WebSocket not initialized"),
      removeEventListener: () => console.warn("WebSocket not initialized"),
      disconnect: () => console.warn("WebSocket not initialized"),
      reconnect: () => console.warn("WebSocket not initialized"),
    };
  }
  
  return context;
}